import React, { useMemo, useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import {
  Images,
  Plus,
  Sparkles,
  LayoutGrid,
  List,
  RefreshCw,
  Lock,
  EyeOff,
  Droplets,
  ArrowLeft,
} from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import AlbumCard from '../../../atoms/Dashboard/HomePage/AlbumCard'
import Pagination from '../../../atoms/Dashboard/HomePage/Pagination'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import { getAllAlbums } from '../../../../Redux/ApiCalls/Dashboard/AlbumAPI'
import { clearError } from '../../../../Redux/Slice/Auth/AuthSlice'
import {
  clearFlagAlbums,
  setCurrentPage,
} from '../../../../Redux/Slice/Dashboard/AlbumSlice'
import { device } from '../../../atoms/Utlis/media'
import { Albums as AlbumType } from '../../../../Data/album.dto'
import MarketplaceEmptyState, {
  MarketplaceLoadingSpinner,
} from '../../../organisms/Dashboard/Marketplace/MarketplaceEmptyState'
import { usePermissions } from '../../../../hooks/usePermissions'

const gradient =
  'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)'

type FilterKey = 'all' | 'locked' | 'hidden' | 'watermark'
type SortKey = 'newest' | 'oldest' | 'az' | 'za'

const Albums: React.FC = () => {
  const [currentPageState, setCurrentPageState] = useState(1)
  const albumsPerPage = 6
  const [viewMode, setViewMode] = useState<'grid' | 'stack'>('grid')
  const [sortBy, setSortBy] = useState<SortKey>('newest')
  const [filter, setFilter] = useState<FilterKey>('all')
  const {
    albums,
    loading,
    total_projects,
    isUpdate,
    isSearchData,
    currentPage,
    stats: reduxStats,
  } = useAppSelector((state) => state.album)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { hasPermission } = usePermissions()
  const canCreateAlbum = hasPermission('albums.create')
  const isFullPage = location.pathname.includes('/albums/all')

  useEffect(() => {
    if (!loading) {
      setCurrentPageState(currentPage)
    }
  }, [loading, currentPage])

  useEffect(() => {
    if (!isUpdate) return
    dispatch(getAllAlbums({ page: currentPage, filter, sort: sortBy }))
  }, [isUpdate, dispatch, currentPage, filter, sortBy])

  useEffect(() => {
    return () => {
      dispatch(clearError())
      dispatch(clearFlagAlbums())
    }
  }, [dispatch])

  const handlePageChange = (page: number) => {
    setCurrentPageState(page)
    dispatch(setCurrentPage(page))
    dispatch(getAllAlbums({ page, filter, sort: sortBy }))
  }

  const handleFilterChange = (newFilter: FilterKey) => {
    setFilter(newFilter)
    setCurrentPageState(1)
    dispatch(setCurrentPage(1))
    dispatch(getAllAlbums({ page: 1, filter: newFilter, sort: sortBy }))
  }

  const handleSortChange = (newSort: SortKey) => {
    setSortBy(newSort)
    setCurrentPageState(1)
    dispatch(setCurrentPage(1))
    dispatch(getAllAlbums({ page: 1, filter, sort: newSort }))
  }

  const handleRefresh = () => {
    dispatch(getAllAlbums({ page: currentPageState, filter, sort: sortBy }))
  }

  const albumList = useMemo(
    () => ((albums as AlbumType[]) ? [...(albums as AlbumType[])] : []),
    [albums]
  )

  const stats = useMemo(() => {
    if (reduxStats && typeof reduxStats.total === 'number') {
      return reduxStats
    }
    const list = albumList
    return {
      total: typeof total_projects === 'number' ? total_projects : list.length,
      locked: list.filter((a) => !!a.is_locked).length,
      hidden: list.filter((a) => !!a.is_hide).length,
      watermark: list.filter((a) => !!a.activate_watermark).length,
    }
  }, [reduxStats, albumList, total_projects])

  const displayAlbums = useMemo(() => {
    if (isSearchData) {
      const filtered = albumList.filter((a) => {
        if (filter === 'locked') return !!a.is_locked
        if (filter === 'hidden') return !!a.is_hide
        if (filter === 'watermark') return !!a.activate_watermark
        return true
      })

      const parseDate = (d?: string) => {
        if (!d) return 0
        const t = Date.parse(d)
        return Number.isNaN(t) ? 0 : t
      }

      filtered.sort((a, b) => {
        if (sortBy === 'az') return (a.name || '').localeCompare(b.name || '')
        if (sortBy === 'za') return (b.name || '').localeCompare(a.name || '')
        if (sortBy === 'oldest') return parseDate(a.date) - parseDate(b.date)
        return parseDate(b.date) - parseDate(a.date)
      })

      return filtered
    }

    return albumList
  }, [albumList, isSearchData, filter, sortBy])

  const totalCount = stats.total
  const hasAlbums = albumList.length > 0
  const hasFilteredResults = displayAlbums.length > 0
  const totalPages = Math.max(1, Math.ceil((total_projects || 0) / albumsPerPage))
  const showPagination =
    !isSearchData &&
    total_projects > albumsPerPage

  const handleAddAlbum = () => navigate('/dashboard/singleAlbum/true')

  const filterOptions: { key: FilterKey; label: string; icon: React.ReactNode; count?: number }[] = [
    { key: 'all', label: 'All', icon: null, count: stats.total },
    { key: 'locked', label: 'Locked', icon: <Lock size={12} />, count: stats.locked },
    { key: 'hidden', label: 'Hidden', icon: <EyeOff size={12} />, count: stats.hidden },
    { key: 'watermark', label: 'Watermark', icon: <Droplets size={12} />, count: stats.watermark },
  ]

  const gridContent = (
    <>
      {loading && !hasAlbums ? (
        <LoadingWrap>
          <MarketplaceLoadingSpinner padding="48px 22px" />
          <SkeletonGrid $stack={viewMode === 'stack'}>
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </SkeletonGrid>
        </LoadingWrap>
      ) : hasFilteredResults ? (
        <AlbumsGrid $stack={viewMode === 'stack'}>
          {displayAlbums.map((album) => (
            <AlbumCard key={album.id} album={album as AlbumType} />
          ))}
        </AlbumsGrid>
      ) : hasAlbums ? (
        <MarketplaceEmptyState
          padding="48px 22px"
          icon={<Images size={36} strokeWidth={1.5} />}
          title="No matches with current filters"
          description="Try a different filter or clear your selection to see all albums."
          actions={[
            {
              label: 'Clear filters',
              onClick: () => handleFilterChange('all'),
            },
          ]}
          hint={{
            icon: <Sparkles size={13} />,
            text: 'Locked, hidden, and watermark filters narrow the list',
          }}
        />
      ) : (
        <MarketplaceEmptyState
          padding="48px 22px"
          icon={<Images size={36} strokeWidth={1.5} />}
          title="Create your first album"
          description="Add a cover image and start uploading photos for your clients."
          actions={
            canCreateAlbum
              ? [
                  {
                    label: 'Create album',
                    onClick: handleAddAlbum,
                    icon: <Plus size={15} />,
                  },
                ]
              : []
          }
          hint={{
            icon: <Sparkles size={13} />,
            text: 'Share albums with clients using secure access codes',
          }}
        />
      )}
    </>
  )

  const toolbar = (
    <Toolbar>
      <FilterPills>
        {filterOptions.map(({ key, label, icon, count }) => (
          <FilterPill
            key={key}
            type="button"
            $active={filter === key}
            onClick={() => handleFilterChange(key)}
          >
            {icon}
            {label}
            {typeof count === 'number' && <FilterCount $active={filter === key}>{count}</FilterCount>}
          </FilterPill>
        ))}
      </FilterPills>

      <ToolbarRight>
        <Select
          value={sortBy}
          onChange={(e) => handleSortChange(e.target.value as SortKey)}
          aria-label="Sort albums"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="az">A → Z</option>
          <option value="za">Z → A</option>
        </Select>

        <ViewToggle>
          <ViewBtn
            type="button"
            $active={viewMode === 'grid'}
            onClick={() => setViewMode('grid')}
            aria-label="Grid view"
          >
            <LayoutGrid size={15} />
          </ViewBtn>
          <ViewBtn
            type="button"
            $active={viewMode === 'stack'}
            onClick={() => setViewMode('stack')}
            aria-label="Stack view"
          >
            <List size={15} />
          </ViewBtn>
        </ViewToggle>

        <RefreshBtn
          type="button"
          onClick={handleRefresh}
          aria-label="Refresh albums"
        >
          <RefreshCw size={15} />
          Refresh
        </RefreshBtn>
      </ToolbarRight>
    </Toolbar>
  )

  const metaRow = (
    <MetaRow>
      <MetaText>
        {isSearchData ? (
          <>
            Showing <strong>{displayAlbums.length}</strong> result
            {displayAlbums.length === 1 ? '' : 's'}
          </>
        ) : (
          <>
            Showing <strong>{displayAlbums.length}</strong> on this page
            {typeof total_projects === 'number' ? (
              <>
                {' '}
                · Total <strong>{total_projects}</strong>
              </>
            ) : null}
          </>
        )}
      </MetaText>
      {isSearchData && <SearchBadge>Search results</SearchBadge>}
    </MetaRow>
  )

  const addButton = canCreateAlbum ? (
    <AddButton type="button" onClick={handleAddAlbum}>
      <Plus size={16} strokeWidth={2.5} />
      Add album
    </AddButton>
  ) : null

  const statsStrip = (
    <StatsStrip>
      <StatChip
        type="button"
        $active={filter === 'all'}
        onClick={() => handleFilterChange('all')}
      >
        <StatIcon $tone="#c94daa">
          <Images size={16} />
        </StatIcon>
        <div>
          <StatValue>{stats.total}</StatValue>
          <StatLabel>Total</StatLabel>
        </div>
      </StatChip>
      <StatChip
        type="button"
        $active={filter === 'locked'}
        onClick={() => handleFilterChange('locked')}
      >
        <StatIcon $tone="#7c5cbf">
          <Lock size={16} />
        </StatIcon>
        <div>
          <StatValue>{stats.locked}</StatValue>
          <StatLabel>Locked</StatLabel>
        </div>
      </StatChip>
      <StatChip
        type="button"
        $active={filter === 'hidden'}
        onClick={() => handleFilterChange('hidden')}
      >
        <StatIcon $tone="#6b7280">
          <EyeOff size={16} />
        </StatIcon>
        <div>
          <StatValue>{stats.hidden}</StatValue>
          <StatLabel>Hidden</StatLabel>
        </div>
      </StatChip>
      <StatChip
        type="button"
        $active={filter === 'watermark'}
        onClick={() => handleFilterChange('watermark')}
      >
        <StatIcon $tone="#35b7c8">
          <Droplets size={16} />
        </StatIcon>
        <div>
          <StatValue>{stats.watermark}</StatValue>
          <StatLabel>Watermark</StatLabel>
        </div>
      </StatChip>
    </StatsStrip>
  )

  if (isFullPage) {
    return (
      <PageContainer>
        <Hero>
          <HeroGlow />
          <HeroContent>
            <HeroLeft>
              {/* <BackBtn to="/dashboard" aria-label="Go back">
                <ArrowLeft size={18} />
              </BackBtn> */}
              <HeroText>
                <HeroBadge>
                  <Sparkles size={11} />
                  Client galleries
                </HeroBadge>
                <PageTitle className="linear-text">Albums</PageTitle>
                <PageSubtitle>
                  Create, organize, and share photo albums with your clients.
                </PageSubtitle>
              </HeroText>
            </HeroLeft>
            {addButton}
          </HeroContent>
        </Hero>

        {statsStrip}

        <Panel>
          {toolbar}
          {gridContent}
          {metaRow}
          {showPagination && (
            <Pagination
              currentPage={currentPageState}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </Panel>
      </PageContainer>
    )
  }

  return (
    <SectionContainer>
      <SectionHeader>
        <HeaderLeft>
          <HeaderIcon>
            <Images size={18} />
          </HeaderIcon>
          <HeaderTextBlock>
            <SectionTitle className="linear-text">Albums</SectionTitle>
            <HeaderSubtext>Manage client photo galleries</HeaderSubtext>
          </HeaderTextBlock>
          <CountPill>{totalCount} total</CountPill>
        </HeaderLeft>
        {addButton}
      </SectionHeader>

      <HomeStatsRow>
        <HomeStatChip type="button" $active={filter === 'locked'} onClick={() => handleFilterChange(filter === 'locked' ? 'all' : 'locked')}>
          <Lock size={13} />
          {stats.locked} locked
        </HomeStatChip>
        <HomeStatChip type="button" $active={filter === 'hidden'} onClick={() => handleFilterChange(filter === 'hidden' ? 'all' : 'hidden')}>
          <EyeOff size={13} />
          {stats.hidden} hidden
        </HomeStatChip>
        <HomeStatChip type="button" $active={filter === 'watermark'} onClick={() => handleFilterChange(filter === 'watermark' ? 'all' : 'watermark')}>
          <Droplets size={13} />
          {stats.watermark} watermark
        </HomeStatChip>
      </HomeStatsRow>

      <Panel>
        {toolbar}
        {gridContent}
        {metaRow}
        {showPagination && (
          <Pagination
            currentPage={currentPageState}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </Panel>
    </SectionContainer>
  )
}

export default Albums

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

const PageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-sizing: border-box;
  font-family: Urbanist, sans-serif;
  animation: ${fadeUp} 0.35s ease-out;
`

const SectionContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: Urbanist, sans-serif;
`

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 22px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.18) 0%,
    rgba(206, 86, 176, 0.14) 55%,
    rgba(124, 77, 255, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
`

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -50%;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`

const HeroLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
`

const BackBtn = styled(Link)`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #171717;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(23, 23, 23, 0.08);
  transition: all 0.15s ease;
  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.25);
  }
`

const HeroText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`

const PageSubtitle = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 520px;
`

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  transition: transform 0.15s ease;
  flex-shrink: 0;
  &:hover {
    transform: translateY(-1px);
  }
`

const StatsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const StatChip = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: ${(p) => (p.$active ? 'rgba(201, 77, 170, 0.06)' : '#fff')};
  border: 1px solid ${(p) => (p.$active ? 'rgba(201, 77, 170, 0.28)' : 'rgba(23, 23, 23, 0.06)')};
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.22);
  }
`

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`

const StatValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`

const Panel = styled.div`
  background: #fff;
  border-radius: 20px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  flex: 1;
  min-height: 0;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201, 77, 170, 0.1);
  color: #c94daa;
  flex-shrink: 0;
`

const HeaderTextBlock = styled.div`
  min-width: 0;
`

const SectionTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
`

const HeaderSubtext = styled.p`
  margin: 2px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #c94daa;
  white-space: nowrap;
`

const HomeStatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const HomeStatChip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? 'rgba(201, 77, 170, 0.3)' : 'rgba(23, 23, 23, 0.08)')};
  background: ${(p) => (p.$active ? 'rgba(201, 77, 170, 0.1)' : '#fff')};
  color: ${(p) => (p.$active ? '#c94daa' : 'rgba(23, 23, 23, 0.65)')};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
  transition: all 0.15s ease;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const FilterPills = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const FilterPill = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? 'rgba(201, 77, 170, 0.3)' : 'rgba(23, 23, 23, 0.08)')};
  background: ${(p) => (p.$active ? 'rgba(201, 77, 170, 0.1)' : '#fafafa')};
  cursor: pointer;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => (p.$active ? '#c94daa' : 'rgba(23, 23, 23, 0.65)')};
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
  }
`

const FilterCount = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  background: ${(p) => (p.$active ? 'rgba(201, 77, 170, 0.18)' : 'rgba(23, 23, 23, 0.06)')};
  color: ${(p) => (p.$active ? '#c94daa' : 'rgba(23, 23, 23, 0.55)')};
`

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const Select = styled.select`
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #171717;
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: rgba(201, 77, 170, 0.35);
  }
`

const ViewToggle = styled.div`
  display: flex;
  background: rgba(23, 23, 23, 0.04);
  border-radius: 10px;
  padding: 3px;
  gap: 3px;
`

const ViewBtn = styled.button<{ $active?: boolean }>`
  width: 34px;
  height: 32px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => (p.$active ? '#fff' : 'rgba(23, 23, 23, 0.55)')};
  background: ${(p) => (p.$active ? gradient : 'transparent')};
  transition: all 0.15s ease;
`

const RefreshBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: #fff;
  cursor: pointer;
  font-family: Urbanist, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.75);
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
`

const AlbumsGrid = styled.div<{ $stack?: boolean }>`
  display: grid;
  grid-template-columns: ${(p) => (p.$stack ? '1fr' : 'repeat(3, 1fr)')};
  gap: ${(p) => (p.$stack ? '20px' : '24px')};
  width: 100%;

  @media ${device.desktop} {
    grid-template-columns: ${(p) =>
    p.$stack ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))'};
  }
  @media ${device.mobileL} {
    grid-template-columns: 1fr;
  }
`

const LoadingWrap = styled.div`
  position: relative;
`

const SkeletonGrid = styled.div<{ $stack?: boolean }>`
  display: grid;
  grid-template-columns: ${(p) => (p.$stack ? '1fr' : 'repeat(3, 1fr)')};
  gap: 24px;
  margin-top: 12px;
  opacity: 0.5;

  @media ${device.desktop} {
    grid-template-columns: ${(p) =>
    p.$stack ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))'};
  }
  @media ${device.mobileL} {
    grid-template-columns: 1fr;
  }
`

const SkeletonCard = styled.div`
  width: 100%;
  height: 280px;
  border-radius: 20px;
  background: linear-gradient(
    90deg,
    rgba(23, 23, 23, 0.04) 0%,
    rgba(23, 23, 23, 0.08) 40%,
    rgba(23, 23, 23, 0.04) 80%
  );
  background-size: 200% 100%;
  animation: shimmer 1.1s ease-in-out infinite;

  @keyframes shimmer {
    0% {
      background-position: 0% 0%;
    }
    100% {
      background-position: -200% 0%;
    }
  }
`

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding-top: 4px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
`

const MetaText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.6);

  strong {
    font-weight: 700;
    color: #171717;
  }
`

const SearchBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Urbanist, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #c94daa;
`
